<template>
  <div class="farm-center-list">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ t('farmList.loading') }}</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ t('farmList.error') }}: {{ error }}</p>
      <AeButton @click="$emit('retry')">{{ t('farmList.retry') }}</AeButton>
    </div>

    <div v-else-if="!hasFarmCenters" class="empty-state">
      <AeCard class="empty-card">
        <h2>{{ t('farmList.welcome') }}</h2>
        <p>{{ t('farmList.noFarms') }}</p>
        <p v-if="canCreate">{{ t('farmList.createFirst') }}</p>
        <AeButton v-if="canCreate" variant="primary" data-testid="btn-new-farm-center" @click="$emit('create')">
          {{ t('farmList.createFirstBtn') }}
        </AeButton>
      </AeCard>
    </div>

    <div v-else class="content">
      <Teleport v-if="canCreate && actionsTargetId && actionsReady" :to="`#${actionsTargetId}`">
        <AeButton variant="primary" data-testid="btn-new-farm-center" @click="$emit('create')">
          <AeIcon name="Plus" :size="16" />
          {{ t('farmList.newFarm') }}
        </AeButton>
      </Teleport>

      <div ref="splitRef" class="split-container">
        <AePanelSplit
          :panels="[{ label: t('farmList.mapPanel'), icon: 'Map' }, { label: t('farmList.listPanel'), icon: 'List' }]"
          :height="panelHeight"
          :ratios="['50%', '50%']"
          :breakpoint="768"
          :default-panel="isMobile ? 1 : 0"
        >
          <!-- Pannello 0: mappa -->
          <template #panel-0>
            <div class="map-panel">
              <FarmCenterMap
                :farm-centers="farmCenters"
                :selected-id="selectedFarmId"
                :height="mapHeight"
                @farm-click="handleFarmClick"
              />
            </div>
          </template>

          <!-- Pannello 1: lista aziende -->
          <template #panel-1>
            <div class="cards-panel">
              <div class="search-box">
                <AeIcon name="Search" :size="16" class="search-icon" />
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t('farmList.searchPlaceholder')"
                  class="search-input"
                />
                <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
                  <AeIcon name="X" :size="14" />
                </button>
              </div>

              <div class="sort-bar">
                <span class="sort-label">{{ t('farmList.sortBy') }}</span>
                <button
                  v-for="opt in sortOptions"
                  :key="opt.value"
                  class="sort-btn"
                  :class="{ active: sortBy === opt.value }"
                  @click="toggleSort(opt.value)"
                >
                  {{ opt.label }}
                  <span v-if="sortBy === opt.value" class="sort-arrow">{{ sortAsc ? '↑' : '↓' }}</span>
                </button>
              </div>

              <AeCard
                v-for="farm in filteredFarmCenters"
                :key="farm.id_farm_center"
                :hoverable="true"
                class="farm-card"
                :class="{ 'farm-card--selected': selectedFarmId === farm.id_farm_center }"
                @click="$emit('view', farm)"
              >
                <template #header>
                  <div class="farm-header">
                    <h3>
                      <AeIcon v-if="isStarred(farm.id_farm_center)" name="Star" :size="14" class="star-icon" />
                      {{ farm.name }}
                    </h3>
                    <div class="farm-actions">
                      <button
                        class="btn-icon"
                        :class="{ 'btn-starred': isStarred(farm.id_farm_center) }"
                        :title="isStarred(farm.id_farm_center) ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'"
                        @click.stop="toggleStar(farm.id_farm_center)"
                      >
                        <AeIcon :name="isStarred(farm.id_farm_center) ? 'Star' : 'StarOff'" :size="15" />
                      </button>
                      <button
                        class="btn-icon"
                        title="Archivia"
                        @click.stop="toggleArchive(farm.id_farm_center)"
                      >
                        <AeIcon name="Archive" :size="15" />
                      </button>
                      <button
                        class="btn-icon"
                        title="Modifica"
                        @click.stop="$emit('edit', farm)"
                      >
                        <AeIcon name="Pencil" :size="16" />
                      </button>
                      <button
                        v-if="canDelete(farm.id_farm_center)"
                        class="btn-icon btn-danger"
                        title="Elimina"
                        @click.stop="$emit('delete', farm)"
                      >
                        <AeIcon name="Trash2" :size="16" />
                      </button>
                    </div>
                  </div>
                </template>

                <div class="farm-body">
                  <p class="farm-location">
                    <AeIcon name="MapPin" :size="14" />
                    {{ farm.place || t('farmList.noAddress') }}
                  </p>

                  <div class="farm-meta-row">
                    <div v-if="farm.lat && (farm.lon || farm.lng)" class="farm-coords">
                      <AeIcon name="Navigation" :size="12" />
                      <small>{{ farm.lat.toFixed(4) }}, {{ (farm.lon || farm.lng).toFixed(4) }}</small>
                    </div>
                    <div v-if="getSeasonCount(farm.id_farm_center) > 0" class="farm-seasons">
                      <AeIcon name="Calendar" :size="12" />
                      <small>{{ getSeasonCount(farm.id_farm_center) }} {{ t('farmList.seasons') }}</small>
                    </div>
                  </div>

                  <div class="farm-stats">
                    <div class="stat-item">
                      <AeIcon name="Grid3x3" :size="14" />
                      <strong>{{ getPlotCount(farm.id_farm_center) }}</strong>
                      <span>{{ t('farmList.plots') }}</span>
                    </div>
                    <div class="stat-item">
                      <AeIcon name="Maximize2" :size="14" />
                      <strong>{{ getFarmArea(farm.id_farm_center) }}</strong>
                      <span>{{ t('farmList.hectares') }}</span>
                    </div>
                  </div>
                  <div v-if="farmSyncMap[farm.id_farm_center] || farm.last_sync_at" class="farm-sync-info">
                    <AeIcon name="RefreshCw" :size="11" />
                    {{ formatLastSync(farmSyncMap[farm.id_farm_center] || farm.last_sync_at) }}
                  </div>
                </div>
              </AeCard>
              <!-- Sezione archiviate -->
              <div v-if="archivedFarmCenters.length > 0" class="archived-section">
                <button class="archived-toggle" @click="showArchived = !showArchived">
                  <AeIcon name="Archive" :size="14" />
                  Archiviate ({{ archivedFarmCenters.length }})
                  <AeIcon :name="showArchived ? 'ChevronUp' : 'ChevronDown'" :size="14" />
                </button>
                <template v-if="showArchived">
                  <AeCard
                    v-for="farm in archivedFarmCenters"
                    :key="farm.id_farm_center"
                    class="farm-card farm-card--archived"
                    @click="$emit('view', farm)"
                  >
                    <template #header>
                      <div class="farm-header">
                        <h3>{{ farm.name }}</h3>
                        <div class="farm-actions">
                          <button
                            class="btn-icon"
                            title="Ripristina"
                            @click.stop="toggleArchive(farm.id_farm_center)"
                          >
                            <AeIcon name="ArchiveRestore" :size="15" />
                          </button>
                        </div>
                      </div>
                    </template>
                    <p class="farm-location">
                      <AeIcon name="MapPin" :size="14" />
                      {{ farm.place || t('farmList.noAddress') }}
                    </p>
                  </AeCard>
                </template>
              </div>
            </div>
          </template>
        </AePanelSplit>
      </div>
    </div>

    <!-- Parla con Poderi — floating action button -->
    <button
      v-if="appStore.hasFeatureFlag('parlaconpoderi')"
      class="voice-fab"
      title="Parla con Poderi"
      @click="router.push('/parlacon')"
    >
      <AeIcon name="Mic" :size="22" />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AeButton, AeCard, AeIcon, AePanelSplit } from '@aevue/components'
import FarmCenterMap from './FarmCenterMap.vue'
import { useAvailableHeight } from '../composables/useAvailableHeight'
import { useUserPreferences } from '../composables/useUserPreferences'
import { useAppStore } from '../stores/app'
import { usePoderiStore } from '../stores/poderiStore'

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()
const poderiStore = usePoderiStore()
const { loadPreferences, isStarred, isArchived, toggleStar, toggleArchive } = useUserPreferences()

const props = defineProps({
  farmCenters: { type: Array, default: () => [] },
  plots: { type: Array, default: () => [] },
  seasons: { type: Array, default: () => [] },
  farmSyncMap: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  hasFarmCenters: { type: Boolean, default: false },
  farmCentersCount: { type: Number, default: 0 },
  breadcrumbs: { type: Array, default: () => [] },
  // false per org_external (UCI): nessun pulsante "Nuova azienda"
  canCreate: { type: Boolean, default: true }
})

defineEmits(['create', 'edit', 'delete', 'view', 'retry'])

const MOBILE_BREAKPOINT = 768

const splitRef = ref(null)
const { availableHeight } = useAvailableHeight(splitRef, { mobileHeight: null })

const windowWidth = ref(window.innerWidth)
const isMobile = computed(() => windowWidth.value <= MOBILE_BREAKPOINT)
const MOBILE_EXTRA_OFFSET = 52  // spazio occupato da UI sopra il component (header, ecc.)
const MOBILE_TABBAR_HEIGHT = 52  // altezza della tabbar Mappa/Lista dentro AePanelSplit

const panelHeight = computed(() =>
  isMobile.value ? availableHeight.value - MOBILE_EXTRA_OFFSET : availableHeight.value
)
const mapHeight = computed(() =>
  (isMobile.value ? panelHeight.value - MOBILE_TABBAR_HEIGHT : panelHeight.value) + 'px'
)

function onResize() {
  windowWidth.value = window.innerWidth
}
const actionsReady = ref(false)
const setHeader = inject('poderiLayoutSetHeader', null)
const clearHeader = inject('poderiLayoutClearHeader', null)
const actionsTargetId = inject('poderiLayoutActionsId', null)

watch(() => props.farmCentersCount, (count) => {
  setHeader?.({ title: t('farmList.title'), subtitle: t('farmList.farmsCount', { count }), breadcrumbs: props.breadcrumbs })
}, { immediate: true })

onMounted(() => {
  window.addEventListener('resize', onResize)
  actionsReady.value = true
  const uid = appStore.user?.uid
  if (uid) loadPreferences(uid)
})
onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  clearHeader?.()
})

const searchQuery = ref('')
const selectedFarmId = ref(null)
const sortBy = ref('name')
const sortAsc = ref(true)

const sortOptions = computed(() => [
  { value: 'name', label: t('farmList.sortName') },
  { value: 'area', label: t('farmList.sortArea') },
  { value: 'sync', label: t('farmList.sortSync') },
])

function toggleSort(value) {
  if (sortBy.value === value) {
    sortAsc.value = !sortAsc.value
  } else {
    sortBy.value = value
    sortAsc.value = true
  }
}

const showArchived = ref(false)

function _sortFarms(list) {
  const dir = sortAsc.value ? 1 : -1
  return [...list].sort((a, b) => {
    if (sortBy.value === 'area') return dir * (_farmAreaM2(a.id_farm_center) - _farmAreaM2(b.id_farm_center))
    if (sortBy.value === 'sync') {
      const ta = new Date(props.farmSyncMap[a.id_farm_center] || a.last_sync_at || 0).getTime()
      const tb = new Date(props.farmSyncMap[b.id_farm_center] || b.last_sync_at || 0).getTime()
      return dir * (ta - tb)
    }
    const na = (a.name || '').trim().replace(/\s+/g, ' ').toLowerCase()
    const nb = (b.name || '').trim().replace(/\s+/g, ' ').toLowerCase()
    return dir * na.localeCompare(nb, 'it')
  })
}

const _visibleFarms = computed(() => {
  let list = props.farmCenters
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(f =>
      (f.name && f.name.toLowerCase().includes(q)) ||
      (f.place && f.place.toLowerCase().includes(q))
    )
  }
  return list
})

const filteredFarmCenters = computed(() => {
  const active = _visibleFarms.value.filter(f => !isArchived(f.id_farm_center))
  const sorted = _sortFarms(active)
  // starred prima solo in modalità alfabetica
  if (sortBy.value === 'name') {
    const starred = sorted.filter(f => isStarred(f.id_farm_center))
    const normal  = sorted.filter(f => !isStarred(f.id_farm_center))
    return [...starred, ...normal]
  }
  return sorted
})

const archivedFarmCenters = computed(() =>
  _sortFarms(_visibleFarms.value.filter(f => isArchived(f.id_farm_center)))
)

function _farmAreaM2(farmId) {
  return props.plots
    .filter(p => p.id_farm_center === farmId)
    .reduce((sum, p) => sum + (p.area || 0), 0)
}

function getFarmArea(farmId) {
  const m2 = _farmAreaM2(farmId)
  if (!m2) return '—'
  return (m2 / 10000).toFixed(2)
}

function formatLastSync(ts) {
  if (!ts) return ''
  const diff = Date.now() - new Date(ts).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'oggi'
  if (days === 1) return 'ieri'
  if (days < 30) return `${days}g fa`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}m fa`
  return `${Math.floor(months / 12)}a fa`
}

function getPlotCount(farmId) {
  return props.plots.filter(p => p.id_farm_center === farmId).length
}

function getSeasonCount(farmId) {
  return props.seasons.filter(s => s.id_farm_center === farmId).length
}

// Elimina visibile solo all'owner (calcolato offline dalla tabella team locale)
const ownerMap = ref({})
watch(() => props.farmCenters.map(f => f.id_farm_center), async (ids) => {
  ownerMap.value = await poderiStore.getFarmCentersOwnerMap(ids)
}, { immediate: true })

function canDelete(farmId) {
  return ownerMap.value[farmId] === true && getPlotCount(farmId) === 0 && getSeasonCount(farmId) <= 1
}

function handleFarmClick(farm) {
  selectedFarmId.value = farm.id_farm_center || farm.id
}
</script>

<style scoped>
.farm-center-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--border-color, #eee);
  border-top-color: var(--primary-color, #4299e1);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.content {
  display: flex;
  flex-direction: column;
}

.split-container {
  flex: 1;
}

/* Mappa */
.map-panel {
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-left: 1rem;
}

/* Lista aziende */
.cards-panel {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-right: 14px;
}

/* Scrollbar personalizzata */
.cards-panel::-webkit-scrollbar {
  width: 6px;
}

.cards-panel::-webkit-scrollbar-track {
  background: transparent;
}

.cards-panel::-webkit-scrollbar-thumb {
  background: var(--border-color, #cbd5e0);
  border-radius: 3px;
}

/* Ricerca */
.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--card-bg, rgba(255, 255, 255, 0.6));
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  flex-shrink: 0;
}

.search-icon {
  color: var(--text-muted, #999);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem; /* >= 16px: prevents iOS Safari viewport zoom on focus */
  color: var(--text-primary, #2d3748);
}

.search-input::placeholder {
  color: var(--text-muted, #999);
}

.search-clear {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--text-muted, #999);
  display: flex;
  align-items: center;
}

.search-clear:hover {
  color: var(--text-primary, #555);
}

/* Ordinamento */
.sort-bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
}

.sort-label {
  font-size: 0.75rem;
  color: var(--text-muted, #999);
}

.sort-btn {
  font-size: 0.75rem;
  padding: 3px 10px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 20px;
  background: transparent;
  color: var(--text-secondary, #718096);
  cursor: pointer;
  transition: all 0.15s;
}

.sort-btn:hover {
  border-color: var(--primary-color, #4299e1);
  color: var(--primary-color, #4299e1);
}

.sort-btn.active {
  background: var(--primary-color, #4299e1);
  border-color: var(--primary-color, #4299e1);
  color: #fff;
}

/* Card singola azienda */
.farm-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  flex-shrink: 0;
}

.farm-card:hover {
  border-color: var(--primary-color, #4299e1);
  transform: translateY(-1px);
}

.farm-card--selected {
  border-color: var(--primary-color, #4299e1);
  box-shadow: 0 0 0 1px var(--primary-color, #4299e1);
}

.farm-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.farm-header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.farm-actions {
  display: flex;
  gap: 0.25rem;
}

.btn-icon {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted, #999);
  transition: color 0.2s;
  display: flex;
  align-items: center;
}

.btn-icon:hover { color: var(--text-primary, #555); }
.btn-icon.btn-danger:hover { color: var(--danger-color, #e53e3e); }

.farm-body {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.farm-location {
  font-size: 0.85rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-secondary, #718096);
}

.farm-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.farm-coords {
  font-size: 0.8rem;
  color: var(--text-muted, #999);
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.farm-seasons {
  font-size: 0.8rem;
  color: var(--text-muted, #999);
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: auto;
}

.farm-stats {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color, #f5f5f5);
}

.stat-item {
  display: flex;
  gap: 0.3rem;
  align-items: center;
  font-size: 0.8rem;
}

.stat-item strong {
  font-size: 1rem;
  color: var(--primary-color, #4299e1);
}

.stat-item span {
  font-size: 0.7rem;
  color: var(--text-muted, #999);
  text-transform: uppercase;
}

.farm-sync-info {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  color: var(--text-muted, #aaa);
  margin-top: 0.3rem;
}

.btn-starred { color: #d69e2e !important; }
.star-icon { color: #d69e2e; margin-right: 0.25rem; }

.archived-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px dashed var(--border-color, #e2e8f0);
}

.archived-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  font-size: 0.8rem;
  color: var(--text-muted, #999);
  cursor: pointer;
  padding: 0.25rem 0;
}

.archived-toggle:hover { color: var(--text-secondary, #718096); }

.farm-card--archived {
  opacity: 0.6;
  border-style: dashed;
}

.voice-fab {
  position: fixed;
  bottom: 4rem;
  right: 1.75rem;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  border: none;
  background: var(--color-primary, #4f7942);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.22);
  cursor: pointer;
  z-index: 800;
  transition: transform 0.15s, box-shadow 0.15s;
}

.voice-fab:hover {
  transform: scale(1.07);
  box-shadow: 0 6px 18px rgba(0,0,0,0.28);
}
</style>
